export const ENDPOINT = (store_slug: string) =>
  `api/app/merchants/${store_slug}/checkout/place-order`;

export type MakeOrderAddon = {
  id: string; // id do addon
  addon_name: string; // nome do addon
  addon_quantity: number; // quantidade do addon
};

// O item mantém os campos, mas os addons agora usam a tipagem ajustada acima.
export type MakeOrderItem = {
  id: string; // id do item
  quantity: number; // quantidade do item
  observation: string; // observação do item
  addons: MakeOrderAddon[]; // lista de addons
  combo_choices?: MakeOrderComboChoice[]; // escolhas do combo configurável (PRAT-20)
};

// Escolha de um grupo do combo configurável (PRAT-20). Na linha do combo os
// `addons` vão vazios; os complementos da escolha vão aqui.
export type MakeOrderComboChoice = {
  group_id: string;
  item_id: string;
  quantity: number;
  addons: MakeOrderAddon[];
};

// O método de pagamento com suporte a cartão de crédito
export type MakeOrderPaymentMethod = {
  id: number | string; // id do método de pagamento (number) ou "card_{uuid}" (string)
  name?: string; // nome do método de pagamento
  icon?: string; // ícone do método de pagamento
  card_id?: string; // id do cartão salvo (obrigatório quando id = 900)
  installments?: number; // número de parcelas (1-12, padrão: 1)
  // CVV do cartão salvo (3 ou 4 dígitos), só quando a API pede confirmação.
  // Sensível: vive só em memória no envio; nunca no form nem no localStorage.
  card_cvv?: string;
};

// Desconto por forma de pagamento persistido junto ao pedido (issue 157).
//
// No checkout do App o desconto é sempre percentual. `value` é o percentual
// aplicado (0–100) e `amount` é o valor absoluto (em reais) recalculado para o
// subtotal no momento da confirmação. Estes valores partem do cliente e NÃO são
// confiáveis: o backend deve revalidá-los contra a configuração da forma de
// pagamento antes de compor o total (OWASP A08 / A04 — não confiar em valor
// monetário calculado no cliente).
export type MakeOrderPaymentDiscount = {
  type: 'percentage'; // único tipo aceito no checkout do App
  value: number; // percentual aplicado sobre o subtotal (0–100)
  amount: number; // valor absoluto (em reais) do desconto para o subtotal atual
  label?: string; // rótulo opcional definido pelo backend (ex.: "Desconto PIX")
};

// Ajustado para corresponder ao body enviado
export type MakeOrderPaymentInfo = {
  coupon_id?: string; // id do cupom
  payment_methods: MakeOrderPaymentMethod[]; // lista de métodos de pagamento
  cash_change_amount?: number; // valor da nota em number
  // Desconto concedido pela forma de pagamento selecionada. Nunca acumula com
  // cupom (exclusão mútua): quando há cupom aplicado, este campo é omitido.
  payment_discount?: MakeOrderPaymentDiscount;
  // Clube pratiGO (PRAT-27): pontos usados no pedido (soma dos itens resgatados).
  // O valor do desconto é calculado no servidor (pontos / multiplicador).
  loyalty_points?: number;
  // Carteira pratiGO (PRAT-89): o saldo da carteira da loja paga o que resta do
  // pedido. O valor é calculado no servidor; com `use_wallet` e a carteira
  // cobrindo tudo, `payment_methods` vai vazio.
  use_wallet?: boolean;
};

// Desconto de retirada persistido junto ao pedido (issue 338).
//
// Só existe em pedidos de RETIRADA (delivery_type = 2), quando a loja oferece um
// percentual de desconto. `percent` é o percentual aplicado (0–100) e `amount` é
// o valor absoluto (em reais) recalculado para o subtotal no momento da
// confirmação. Assim como o desconto de pagamento, estes valores partem do
// cliente e NÃO são confiáveis: o backend deve revalidá-los contra a
// configuração da loja antes de compor o total (OWASP A08 / A04 — não confiar em
// valor monetário calculado no cliente).
export type MakeOrderPickupDiscount = {
  percent: number; // percentual aplicado sobre o subtotal (0–100)
  amount: number; // valor absoluto (em reais) do desconto para o subtotal atual
};

// A entrega permanece igual ao exemplo.
export type MakeOrderDeliveryInfo = {
  address_id?: string; // id do endereço (opcional para retirada)
  delivery_type: number; // tipo de entrega (1 para entrega, 2 para retirada)
  // Desconto de retirada concedido pela loja em pedidos de retirada (issue 338).
  // Omitido quando não é retirada ou não há desconto configurado.
  pickup_discount?: MakeOrderPickupDiscount;
};

// Campos de agendamento (quando has_schedule = true)
export type MakeOrderScheduleInfo = {
  has_schedule?: boolean; // se tem agendamento
  schedule_date?: string; // data do agendamento (ex: "2025-10-25 06:00:00")
  schedule_slot?: string; // slot de horário (ex: "06:00-07:00")
};
